import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FileBarChart, Download, Lock, Unlock, Loader2, Plus, X } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime, formatDate } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

interface Pnl {
  from: string
  to: string
  revenue_fils: number
  cogs_fils: number
  gross_margin_fils: number
  gross_margin_pct: number | null
  operating_expenses_fils: number
  shrinkage_fils: number
  net_income_fils: number
  order_count: number
  from_snapshot: boolean
}

interface Period {
  id: string
  period_start: string
  period_end: string
  status: 'open' | 'closed'
  revenue_fils: number
  cogs_fils: number
  gross_margin_fils: number
  operating_expenses_fils: number
  shrinkage_fils: number
  net_income_fils: number
  notes: string | null
  closed_at: string | null
  closed_by_admin_id: string | null
  created_at: string
}

const START_OF_MONTH = () => {
  const d = new Date()
  d.setDate(1)
  return d.toISOString().slice(0, 10)
}
const END_OF_MONTH = () => {
  const d = new Date()
  const eom = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return eom.toISOString().slice(0, 10)
}

export default function PnL() {
  const { can } = useAuth()
  const canClose = can(['super_admin', 'operations', 'accounting'])
  const [from, setFrom] = useState(START_OF_MONTH())
  const [to, setTo] = useState(END_OF_MONTH())
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isLoading } = useQuery<Pnl>({
    queryKey: ['finance-pl', from, to],
    queryFn: () => api.get(`/finance/pl?from=${from}&to=${to}`).then(r => r.data),
  })

  const { data: periodsData } = useQuery<{ periods: Period[] }>({
    queryKey: ['finance-periods'],
    queryFn: () => api.get('/finance/periods').then(r => r.data),
  })
  const periods = periodsData?.periods ?? []

  function downloadCsv() {
    window.open(`${api.defaults.baseURL}/finance/pl.csv?from=${from}&to=${to}`, '_blank')
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileBarChart size={20} className="text-[#c41e3a]" />
            Profit &amp; Loss
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Live P&amp;L computed from the ledger by default. Closing a period snapshots the numbers so late-arriving invoices don't retro-change a signed-off month.
          </p>
        </div>
        <button onClick={downloadCsv}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#3a3a3a] text-gray-200 text-sm font-semibold hover:bg-white/5">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Range picker + preset chips */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wide">From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputCls + ' mt-1'} />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wide">To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputCls + ' mt-1'} />
        </div>
        <div className="flex flex-wrap gap-1.5 pt-4">
          <PresetChip label="This month" onClick={() => { setFrom(START_OF_MONTH()); setTo(END_OF_MONTH()) }} />
          <PresetChip label="Last month" onClick={() => {
            const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1)
            const start = d.toISOString().slice(0, 10)
            const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10)
            setFrom(start); setTo(end)
          }} />
          <PresetChip label="This quarter" onClick={() => {
            const d = new Date()
            const q = Math.floor(d.getMonth() / 3)
            const start = new Date(d.getFullYear(), q * 3, 1).toISOString().slice(0, 10)
            const end = new Date(d.getFullYear(), q * 3 + 3, 0).toISOString().slice(0, 10)
            setFrom(start); setTo(end)
          }} />
          <PresetChip label="YTD" onClick={() => {
            const d = new Date()
            const start = new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10)
            const end = new Date().toISOString().slice(0, 10)
            setFrom(start); setTo(end)
          }} />
        </div>
        {data && (
          <div className="ml-auto text-xs uppercase tracking-wide pt-4">
            {data.from_snapshot ? (
              <span className="px-2 py-1 rounded bg-green-500/15 text-green-400 flex items-center gap-1"><Lock size={12} /> Closed snapshot</span>
            ) : (
              <span className="px-2 py-1 rounded bg-blue-500/15 text-blue-400 flex items-center gap-1"><Unlock size={12} /> Live from ledger</span>
            )}
          </div>
        )}
      </div>

      {/* P&L body */}
      {isLoading ? (
        <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
      ) : data ? (
        <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6 space-y-1">
          <PlRow label="Revenue" value={data.revenue_fils} bold />
          <PlDivider />
          <PlRow label="Cost of Goods Sold" value={-data.cogs_fils} indent />
          <PlRow label="Gross Margin" value={data.gross_margin_fils} bold color="text-white"
                 sublabel={data.gross_margin_pct != null ? `${(data.gross_margin_pct * 100).toFixed(1)}%` : undefined} />
          <PlDivider />
          <PlRow label="Operating Expenses" value={-data.operating_expenses_fils} indent
                 hint="Supplier invoices dated in period (accrual)" />
          <PlRow label="Shrinkage" value={-data.shrinkage_fils} indent
                 hint="Missing / broken stock via count corrections" />
          <PlDivider />
          <PlRow label="Net Income" value={data.net_income_fils} bold big
                 color={data.net_income_fils >= 0 ? 'text-green-400' : 'text-red-400'} />
          <div className="pt-3 mt-3 border-t border-white/10 text-xs text-gray-500 flex items-center justify-between">
            <span>{data.order_count} orders in period</span>
            <span>{formatDate(data.from)} → {formatDate(data.to)}</span>
          </div>
        </div>
      ) : null}

      {/* Accounting periods */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-white">Accounting periods</div>
            <div className="text-xs text-gray-500 mt-0.5">Closing a period locks the numbers verbatim so late supplier invoices don't retro-change last month.</div>
          </div>
          {canClose && (
            <button onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#c41e3a] text-white font-semibold hover:bg-[#a01830]">
              <Plus size={12} /> New period
            </button>
          )}
        </div>
        {periods.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No periods yet — create one to lock a range.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Revenue</th>
                <th className="text-right px-4 py-2">Gross margin</th>
                <th className="text-right px-4 py-2">Net income</th>
                <th className="text-left px-4 py-2">Closed at</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {periods.map(p => (
                <PeriodRow key={p.id} period={p} canClose={canClose} onChanged={() => {}} onLoad={() => { setFrom(p.period_start); setTo(p.period_end) }} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <CreatePeriodModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => setCreateOpen(false)}
        />
      )}
    </div>
  )
}

const inputCls = 'bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]'

function PresetChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="text-[11px] px-2.5 py-1 rounded-full border border-[#3a3a3a] text-gray-300 hover:bg-white/5">
      {label}
    </button>
  )
}

function PlDivider() {
  return <div className="border-t border-white/5 my-1.5" />
}

function PlRow({ label, value, bold, big, indent, color, hint, sublabel }: {
  label: string
  value: number
  bold?: boolean
  big?: boolean
  indent?: boolean
  color?: string
  hint?: string
  sublabel?: string
}) {
  return (
    <div className={`flex items-baseline justify-between py-1 ${indent ? 'pl-6' : ''}`}>
      <div>
        <div className={`${bold ? 'font-semibold' : ''} ${big ? 'text-lg' : 'text-sm'} ${color ?? (bold ? 'text-white' : 'text-gray-300')}`}>{label}</div>
        {hint && <div className="text-[11px] text-gray-600 mt-0.5">{hint}</div>}
      </div>
      <div className={`font-mono ${big ? 'text-xl font-bold' : bold ? 'font-semibold' : ''} ${color ?? 'text-white'} text-right`}>
        {formatCurrency(value)}
        {sublabel && <span className="text-xs text-gray-500 ml-2">({sublabel})</span>}
      </div>
    </div>
  )
}

function PeriodRow({ period, canClose, onLoad }: { period: Period; canClose: boolean; onChanged: () => void; onLoad: () => void }) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState<'close' | 'reopen' | null>(null)
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['finance-periods'] })
    qc.invalidateQueries({ queryKey: ['finance-pl'] })
  }
  async function close() {
    if (!confirm(`Close ${period.period_start} → ${period.period_end}? The numbers get snapshotted and any later data won't retro-change them.`)) return
    setBusy('close')
    try {
      await api.post(`/finance/periods/${period.id}/close`, {})
      invalidate()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      alert(e.response?.data?.message ?? "Couldn't close.")
    } finally { setBusy(null) }
  }
  async function reopen() {
    if (!confirm('Reopen period? Snapshot values are cleared until you close again.')) return
    setBusy('reopen')
    try {
      await api.post(`/finance/periods/${period.id}/reopen`, {})
      invalidate()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      alert(e.response?.data?.message ?? "Couldn't reopen.")
    } finally { setBusy(null) }
  }
  return (
    <tr className="border-t border-white/10">
      <td className="px-4 py-2 text-sm">
        <button onClick={onLoad} className="text-white hover:underline">
          {formatDate(period.period_start)} → {formatDate(period.period_end)}
        </button>
      </td>
      <td className="px-4 py-2">
        <span className={`text-xs px-2 py-0.5 rounded-full ${period.status === 'closed' ? 'bg-green-500/15 text-green-400' : 'bg-blue-500/15 text-blue-400'}`}>
          {period.status === 'closed' ? 'Closed' : 'Open'}
        </span>
      </td>
      <td className="px-4 py-2 text-right text-sm font-mono text-gray-300">{period.status === 'closed' ? formatCurrency(period.revenue_fils) : '—'}</td>
      <td className="px-4 py-2 text-right text-sm font-mono text-gray-300">{period.status === 'closed' ? formatCurrency(period.gross_margin_fils) : '—'}</td>
      <td className={`px-4 py-2 text-right text-sm font-mono ${period.status === 'closed' ? (period.net_income_fils >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-500'}`}>
        {period.status === 'closed' ? formatCurrency(period.net_income_fils) : '—'}
      </td>
      <td className="px-4 py-2 text-xs text-gray-500">{period.closed_at ? formatDateTime(period.closed_at) : <span className="text-gray-600">—</span>}</td>
      <td className="px-4 py-2 text-right">
        {canClose && (period.status === 'open' ? (
          <button disabled={busy !== null} onClick={close}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#c41e3a] text-white font-semibold hover:bg-[#a01830] disabled:opacity-40">
            {busy === 'close' ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} Close
          </button>
        ) : (
          <button disabled={busy !== null} onClick={reopen}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-white/5 border border-[#3a3a3a] text-gray-300 hover:bg-white/10 disabled:opacity-40">
            {busy === 'reopen' ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />} Reopen
          </button>
        ))}
      </td>
    </tr>
  )
}

function CreatePeriodModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const qc = useQueryClient()
  const [start, setStart] = useState(START_OF_MONTH())
  const [end, setEnd] = useState(END_OF_MONTH())
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (end < start) { setError('End must be on/after start'); return }
    setSubmitting(true)
    try {
      await api.post('/finance/periods', { period_start: start, period_end: end, notes: notes.trim() || null })
      qc.invalidateQueries({ queryKey: ['finance-periods'] })
      onCreated()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string; code?: string } } }
      setError(e.response?.data?.code === 'duplicate_period' ? 'A period with this exact range already exists.' : (e.response?.data?.message ?? "Couldn't create."))
    } finally { setSubmitting(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => e.stopPropagation()}>
      <form onSubmit={submit} className="w-full max-w-md bg-[#1a1a1a] border border-white/10 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold">New accounting period</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500">Start</label>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} className={`${inputCls} w-full mt-1`} />
          </div>
          <div>
            <label className="text-xs text-gray-500">End</label>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} className={`${inputCls} w-full mt-1`} />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} className={`${inputCls} w-full mt-1 min-h-[60px]`} />
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]">Cancel</button>
          <button type="submit" disabled={submitting} className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create period'}
          </button>
        </div>
      </form>
    </div>
  )
}
